import { Box, CircularProgress } from "@mui/material";
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getApplicationAction,
  isApplicationFeeSettled,
} from "src/helpers/helperApplications";
import {
  clearAuthReturnTo,
  parsePaymentApplicationId,
} from "src/shared/utilities/authRedirect";
import { useGetApplicationsMutation } from "src/store/services/privateApi";

/**
 * Post-Auth0 landing for payment returnTo. Soft-recover / PrivateRoute stash
 * `/application/payment?applicationId=…` while the resident is mid-pay; after
 * the fee is settled that stash is stale. Sending them back shows a blank
 * name / $0 flash before LeasePayment bounces them — resolve here instead.
 */
const AuthResume = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [getApplications] = useGetApplicationsMutation();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let cancelled = false;
    (async () => {
      const raw = searchParams.get("to") || "/";
      const target = raw.startsWith("/") ? raw : "/";
      clearAuthReturnTo();

      const applicationId = parsePaymentApplicationId(target);
      if (!applicationId) {
        navigate(target, { replace: true });
        return;
      }

      try {
        const res: any = await getApplications({ id: applicationId });
        if (cancelled) return;
        const apps = res?.data;
        const app = Array.isArray(apps)
          ? apps.find((a: any) => String(a.id) === String(applicationId)) ??
            apps[0]
          : apps;

        if (app && isApplicationFeeSettled(app)) {
          const next = getApplicationAction(app);
          navigate(next?.buildPath() ?? "/", { replace: true });
          return;
        }
      } catch {
        // Fail open: unpaid applicants should still reach payment.
      }
      if (!cancelled) navigate(target, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [getApplications, navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress color="primary" size={60} />
    </Box>
  );
};

export default AuthResume;
